import { useState } from 'react';
import {
  FileText,
  Clock,
  Eye,
  Edit2,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Building2,
  Tag,
  Copy,
} from 'lucide-react';
import { DOCUMENT_CATEGORY_LABELS, type MHRDocument, type DocumentCategory } from '../../types/document';
import { AsyncProcessingStatus } from '../Upload';
import { DocumentMetadataEditor } from './DocumentMetadataEditor';
import { DuplicateNotice } from './DuplicateNotice';

interface DocumentCardProps {
  document: MHRDocument;
  asyncProcessing: boolean;
  reprocessingDocId: string | null;
  onViewDocument: (docId: string) => void;
  onGetCostEstimate: (docId: string) => void;
  onAsyncProcessingComplete: (docId: string) => void;
  onReprocessDocument: (docId: string) => void;
  onDocumentUpdated: (doc: MHRDocument) => void;
}

const CATEGORY_COLORS: Partial<Record<DocumentCategory, string>> = {
  medical_records: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  lab_results: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  radiology: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
  pathology: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
  operative_notes: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
  discharge_summary: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
  progress_notes: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
  medication_list: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  referral: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
};

export function DocumentCard({
  document: doc,
  asyncProcessing,
  reprocessingDocId,
  onViewDocument,
  onGetCostEstimate,
  onAsyncProcessingComplete,
  onReprocessDocument,
  onDocumentUpdated,
}: DocumentCardProps) {
  const [editing, setEditing] = useState(false);

  const category = doc.document_category || doc.ai_document_category;
  const categoryLabel = category
    ? DOCUMENT_CATEGORY_LABELS[category as DocumentCategory] || category
    : null;
  const categoryColor = CATEGORY_COLORS[category as DocumentCategory] || 'bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400';

  const facilityName = doc.facility_name || doc.ai_facility_name;
  const description = doc.description || doc.ai_description;
  const isAiOnly = !doc.document_category && !!doc.ai_document_category;

  if (editing) {
    return (
      <DocumentMetadataEditor
        document={doc}
        onSave={(updated) => {
          onDocumentUpdated(updated);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {doc.is_duplicate && <DuplicateNotice />}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
            <FileText size={20} className="text-gray-600 dark:text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{doc.filename}</p>
              {doc.is_duplicate && (
                <span title="Duplicate document"><Copy size={14} className="text-orange-400 flex-shrink-0" /></span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex-wrap">
              <span>{doc.page_count} pages</span>
              <span>{(doc.file_size / 1024 / 1024).toFixed(1)} MB</span>
              {facilityName && (
                <span className="inline-flex items-center gap-1">
                  <Building2 size={12} />
                  {facilityName}
                  {isAiOnly && <span className="text-purple-400" title="AI-suggested">*</span>}
                </span>
              )}
              {doc.document_date && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} />
                  {new Date(doc.document_date).toLocaleDateString()}
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                {description}
                {!doc.description && doc.ai_description && <span className="text-purple-400"> (AI)</span>}
              </p>
            )}
            {/* Tags */}
            {doc.tags && doc.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {doc.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                    <Tag size={10} />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Right side: category badge + status + actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {categoryLabel && (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${categoryColor}`}>
                {categoryLabel}
              </span>
            )}

            {/* Extraction status */}
            {doc.extraction_status === 'pending' && !asyncProcessing && (
              <button
                onClick={() => onGetCostEstimate(doc.id)}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors min-h-[44px] sm:min-h-0"
              >
                Start Processing
              </button>
            )}
            {(doc.extraction_status === 'extracting' ||
              doc.extraction_status === 'ocr_in_progress' ||
              doc.extraction_status === 'vision_verification' ||
              asyncProcessing) && (
              <AsyncProcessingStatus
                documentId={doc.id}
                autoStart={false}
                onComplete={() => onAsyncProcessingComplete(doc.id)}
                onCancel={() => onAsyncProcessingComplete(doc.id)}
              />
            )}
            {doc.extraction_status === 'completed' && (
              <span className="px-3 py-1.5 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg">
                Completed
              </span>
            )}
            {doc.extraction_status === 'completed_with_errors' && (
              <>
                <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <AlertTriangle size={14} />
                  Partial
                </span>
                <button
                  onClick={() => onReprocessDocument(doc.id)}
                  disabled={reprocessingDocId === doc.id}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                >
                  {reprocessingDocId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Reprocess
                </button>
              </>
            )}
            {doc.extraction_status === 'failed' && (
              <>
                <span className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  Failed
                </span>
                <button
                  onClick={() => onReprocessDocument(doc.id)}
                  disabled={reprocessingDocId === doc.id}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                >
                  {reprocessingDocId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Try Again
                </button>
              </>
            )}

            {/* View/Edit buttons */}
            <button
              onClick={() => onViewDocument(doc.id)}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="View document"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="Edit metadata"
            >
              <Edit2 size={16} />
            </button>

            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 ml-1">
              <Clock size={14} />
              {new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
